/**
 * GA4（Firebase Analytics）への行動送信。
 *
 * 同じ測定 ID の計測は、企業サイトに埋め込む Widget では既に動いていた
 * （packages/shared の初期化が gtag を自分で読み込む）。Nomiss 自身の配信ページだけが
 * 繋がっておらず、`pushGtmEvent` が window.dataLayer に積むだけで受け手がいなかった。
 * そのため 2026-10-02 の本番テスト配信では、流入元も商品クリックもカート追加も 1 件も
 * 残らなかった。ここを繋ぐと、流入元・クリック・カート・登録・ファネルは GA4 が持つ。
 *
 * 売上の正本はここではない。GA4 の purchase はクライアント申告で、広告ブロッカーや
 * 同意拒否、送信失敗で落ちる。GMV は commerce_purchases が正本であり、GA4 は
 * 「どの流入元が売れたか」を見るためだけに使う。突合できるよう transaction_id に
 * 注文 ID を入れる。
 *
 * 最大同接とライブ視聴時間は GA4 では取れない（同時接続の概念が無く、動画計測は
 * 終わりのある動画の再生率を前提にしている）。それは stream_presence_samples が持つ。
 */
import {
  type Analytics,
  getAnalytics,
  isSupported,
  logEvent,
  setUserId,
} from 'firebase/analytics';

import { commerceTestPreview } from './commerce-test-preview';
import { app } from './firebase';

let instance: Analytics | null = null;
let startRequested = false;

/** 環境ごとに測定 ID を分ける。混ざると検証の流入が本番の数字に入る */
function measurementId(): string | undefined {
  const id = process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID;
  return id && /^G-[A-Z0-9]{4,20}$/.test(id) ? id : undefined;
}

/**
 * 隔離された検証用ランタイムでは送らない。本番の GA4 プロパティに検証の操作が入ると、
 * 流入元別の数字が後から分離できなくなる。
 */
export function analyticsEnabled(): boolean {
  return typeof window !== 'undefined' && !commerceTestPreview && !!measurementId() && !!app;
}

/** 1 度だけ初期化する。失敗しても画面と買い物は動かし続ける */
export async function startAnalytics(): Promise<void> {
  if (startRequested || !analyticsEnabled()) return;
  startRequested = true;
  try {
    if (!(await isSupported())) return;
    instance = getAnalytics(app!);
  } catch {
    instance = null;
  }
}

function report(name: string, params: Record<string, unknown>): void {
  if (!instance) return;
  try {
    logEvent(instance, name, params);
  } catch {
    // 計測の失敗は買い物を止めない。
  }
}

/** 初期化が終わる前に起きた操作も落とさない。順序は保つ */
const pending: Array<[string, Record<string, unknown>]> = [];
const PENDING_LIMIT = 50;

export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
  if (!analyticsEnabled()) return;
  if (!instance) {
    if (pending.length < PENDING_LIMIT) pending.push([name, params]);
    void startAnalytics().then(() => {
      if (!instance) return;
      while (pending.length) {
        const next = pending.shift();
        if (next) report(next[0], next[1]);
      }
    });
    return;
  }
  report(name, params);
}

/**
 * 匿名で見ていた人が会員になったとき、GA4 側でも同じ人として繋ぐ。
 * ログアウトや別アカウントへの切り替えでは外す。外さないと次に同じ端末を使う人が
 * 前の人に合算される。
 */
export function setAnalyticsUser(uid: string | null): void {
  if (!analyticsEnabled()) return;
  const apply = () => {
    if (!instance) return;
    try {
      setUserId(instance, uid);
    } catch {
      // 同上。
    }
  };
  if (!instance) void startAnalytics().then(apply);
  else apply();
}

/**
 * 決済完了は 1 注文につき 1 回だけ送る。GA4 は transaction_id が重複した purchase を
 * 弾かないので、再読込や復元経路で二重に送ると売上が膨らむ。
 */
export function trackPurchaseOnce(input: {
  transactionId: string;
  value: number;
  tax?: number;
  shipping?: number;
  currency: string;
  streamId?: string;
  items?: Array<Record<string, unknown>>;
}): void {
  if (!analyticsEnabled() || !input.transactionId) return;
  const key = `nomiss-ga4-purchase-${input.transactionId}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
  } catch {
    // 保存できない環境では二重送信を防げないので、送らない方を選ぶ。
    return;
  }
  trackEvent('purchase', {
    transaction_id: input.transactionId,
    value: input.value,
    currency: input.currency,
    ...(input.tax !== undefined ? { tax: input.tax } : {}),
    ...(input.shipping !== undefined ? { shipping: input.shipping } : {}),
    ...(input.streamId ? { stream_id: input.streamId } : {}),
    ...(input.items ? { items: input.items } : {}),
  });
}
